import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  EventAvailable as CoverageIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import SubstituteAssignmentForm from './SubstituteAssignmentForm';
import { 
  getSubstituteAssignments, 
  cancelSubstituteAssignment,
  updateSubstituteAssignment 
} from '../../utils/substituteUtils';
import { useAuth } from '../../context/AuthContext';
import SnackbarMessage from '../SnackbarMessage';
import supabase from '../../supabaseClient';

const STATUS_COLORS = {
  pending: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const STATUS_LABELS = {
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function SubstituteManagement() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Initialize tab value from URL parameter or default to 0
  const getInitialTabValue = () => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab === 'coverage-requests') return 1;
    return 0; // default to 'all-assignments'
  };
  
  const [tabValue, setTabValue] = useState(getInitialTabValue);
  
  // Assignments tab state
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [assignmentToCancel, setAssignmentToCancel] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, upcoming, past
  
  // Coverage requests tab state
  const [coverageRequests, setCoverageRequests] = useState([]);
  const [coverageRequestsLoading, setCoverageRequestsLoading] = useState(true);
  const [coverageFilter, setCoverageFilter] = useState('pending');
  const [assignSubstituteDialogOpen, setAssignSubstituteDialogOpen] = useState(false);
  const [requestToAssign, setRequestToAssign] = useState(null);
  const [substituteTeachers, setSubstituteTeachers] = useState([]);
  const [selectedSubstitute, setSelectedSubstitute] = useState('');
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchAssignments();
    fetchCoverageRequests();
    fetchSubstituteTeachers();
  }, []);

  // Sync tab state with URL changes (browser back/forward)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    const newTabValue = tab === 'coverage-requests' ? 1 : 0;
    if (newTabValue !== tabValue) {
      setTabValue(newTabValue);
    }
  }, [location.search, tabValue]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      // Get assignments for the last 30 days and next 90 days
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 90);
      
      const data = await getSubstituteAssignments(
        dayjs(startDate).format('YYYY-MM-DD'),
        dayjs(endDate).format('YYYY-MM-DD')
      );
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching substitute assignments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load substitute assignments',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCoverageRequests = async () => {
    try {
      setCoverageRequestsLoading(true);
      const { data, error } = await supabase
        .from('coverage_requests')
        .select(`
          *,
          teacher:teacher_id (
            id,
            first_name,
            last_name
          ),
          classes (
            id,
            name,
            shift
          ),
          substitute_teacher:substitute_teacher_id (
            id,
            first_name,
            last_name
          ),
          reviewed_by_admin:reviewed_by (
            id,
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCoverageRequests(data || []);
    } catch (error) {
      console.error('Error fetching coverage requests:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load coverage requests',
        severity: 'error'
      });
    } finally {
      setCoverageRequestsLoading(false);
    }
  };

  const fetchSubstituteTeachers = async () => {
    try {
      const { data, error } = await supabase
        .from('teachers')
        .select('id, first_name, last_name')
        .order('first_name');

      if (error) throw error;
      setSubstituteTeachers(data || []);
    } catch (error) {
      console.error('Error fetching substitute teachers:', error);
    }
  };

  const getAssignmentStatus = (assignment) => {
    // Return the actual status from the database
    return assignment.status;
  };

  const filteredAssignments = assignments.filter(assignment => {
    const status = getAssignmentStatus(assignment);
    
    switch (filter) {
      case 'pending':
        return status === 'pending';
      case 'completed':
        return status === 'completed';
      case 'cancelled':
        return status === 'cancelled';
      default:
        return true;
    }
  });

  const filteredCoverageRequests = coverageRequests.filter(request => {
    switch (coverageFilter) {
      case 'pending':
        return request.status === 'pending';
      case 'approved':
        return request.status === 'approved';
      case 'assigned':
        return request.status === 'assigned';
      case 'rejected':
        return request.status === 'rejected';
      default:
        return true;
    }
  });

  const handleCreateAssignment = () => {
    setEditingAssignment(null);
    setShowForm(true);
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setShowForm(true);
  };

  const handleCancelAssignment = async (assignment) => {
    setAssignmentToCancel(assignment);
    setCancelDialogOpen(true);
  };

  const handleMarkCompleted = async (assignment) => {
    // UI validation: only pending assignments can be marked as completed
    if (assignment.status !== 'pending') {
      setSnackbar({
        open: true,
        message: 'Only pending assignments can be marked as completed',
        severity: 'error'
      });
      return;
    }

    try {
      await updateSubstituteAssignment(assignment.id, {
        status: 'completed'
      });
      setSnackbar({
        open: true,
        message: 'Assignment marked as completed',
        severity: 'success'
      });
      fetchAssignments();
    } catch (error) {
      console.error('Error marking assignment as completed:', error);
      setSnackbar({
        open: true,
        message: 'Failed to mark assignment as completed',
        severity: 'error'
      });
    }
  };

  const confirmCancelAssignment = async () => {
    if (!assignmentToCancel) return;

    // UI validation: only pending assignments can be cancelled
    if (assignmentToCancel.status !== 'pending') {
      setSnackbar({
        open: true,
        message: 'Only pending assignments can be cancelled',
        severity: 'error'
      });
      setCancelDialogOpen(false);
      setAssignmentToCancel(null);
      return;
    }

    try {
      await cancelSubstituteAssignment(
        assignmentToCancel.id,
        'Cancelled by admin'
      );
      setSnackbar({
        open: true,
        message: 'Substitute assignment cancelled successfully',
        severity: 'success'
      });
      fetchAssignments();
    } catch (error) {
      console.error('Error cancelling assignment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to cancel assignment',
        severity: 'error'
      });
    } finally {
      setCancelDialogOpen(false);
      setAssignmentToCancel(null);
    }
  };

  const handleFormSuccess = () => {
    fetchAssignments();
    setShowForm(false);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAssignment(null);
  };

  // Coverage request handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Update URL parameter to persist tab state
    const urlParams = new URLSearchParams(location.search);
    if (newValue === 1) {
      urlParams.set('tab', 'coverage-requests');
    } else {
      urlParams.delete('tab'); // default tab, no parameter needed
    }
    
    const newSearch = urlParams.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    navigate(newPath, { replace: true });
  };

  const handleApproveCoverageRequest = (request) => {
    setRequestToAssign(request);
    setSelectedSubstitute('');
    setAssignSubstituteDialogOpen(true);
  };

  const handleRejectCoverageRequest = (request) => {
    setRequestToReject(request);
    setRejectionReason('');
    setRejectionDialogOpen(true);
  };

  const confirmAssignSubstitute = async () => {
    if (!requestToAssign || !selectedSubstitute) return;

    try {
      // Create substitute assignment
      const assignmentData = {
        class_id: requestToAssign.class_id,
        original_teacher_id: requestToAssign.teacher_id,
        substitute_teacher_id: selectedSubstitute,
        start_date: requestToAssign.start_date,
        end_date: requestToAssign.end_date,
        reason: requestToAssign.reason,
        status: 'pending',
        notes: `Coverage requested: ${requestToAssign.description || 'No additional details'}`,
        created_by: user.id
      };

      const { data: assignmentResult, error: assignmentError } = await supabase
        .from('substitute_assignments')
        .insert([assignmentData])
        .select()
        .single();

      if (assignmentError) throw assignmentError;

      // Update coverage request status
      const { error: updateError } = await supabase
        .from('coverage_requests')
        .update({
          status: 'assigned',
          substitute_teacher_id: selectedSubstitute,
          substitute_assignment_id: assignmentResult.id,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', requestToAssign.id);

      if (updateError) throw updateError;

      setSnackbar({
        open: true,
        message: 'Substitute teacher assigned successfully',
        severity: 'success'
      });

      fetchCoverageRequests();
      fetchAssignments();
    } catch (error) {
      console.error('Error assigning substitute:', error);
      setSnackbar({
        open: true,
        message: 'Failed to assign substitute teacher',
        severity: 'error'
      });
    } finally {
      setAssignSubstituteDialogOpen(false);
      setRequestToAssign(null);
      setSelectedSubstitute('');
    }
  };

  const confirmRejectRequest = async () => {
    if (!requestToReject || !rejectionReason.trim()) return;

    try {
      const { error } = await supabase
        .from('coverage_requests')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason.trim(),
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', requestToReject.id);

      if (error) throw error;

      setSnackbar({
        open: true,
        message: 'Coverage request rejected',
        severity: 'success'
      });

      fetchCoverageRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reject request',
        severity: 'error'
      });
    } finally {
      setRejectionDialogOpen(false);
      setRequestToReject(null);
      setRejectionReason('');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
        <CircularProgress size={isMobile ? 32 : 40} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h5" 
        component="h1" 
        sx={{ 
          mb: { xs: 2, sm: 3 },
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
        }}
      >
        Substitute Teacher Management
      </Typography>

      {/* Tabs */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              py: { xs: 1.5, sm: 2 },
              minWidth: { xs: 'auto', sm: 0 },
              px: { xs: 2, sm: 3 },
            },
          }}
        >
          <Tab
            icon={<AssignmentIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
            label="All Assignments"
            iconPosition="start"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          />
          <Tab
            icon={<CoverageIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
            label="Coverage Requests"
            iconPosition="start"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 ? (
        // All Assignments Tab (existing content)
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'flex-start', sm: 'space-between' }, 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 2, sm: 0 },
          }}>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={handleCreateAssignment}
              fullWidth={isMobile}
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '12px 24px' },
              }}
            >
              Create Assignment
            </Button>
          </Box>

      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Filter</InputLabel>
            <Select
              value={filter}
              label="Filter"
              onChange={(e) => setFilter(e.target.value)}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>All Assignments</MenuItem>
              <MenuItem value="pending" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Pending</MenuItem>
              <MenuItem value="completed" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Completed</MenuItem>
              <MenuItem value="cancelled" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {filteredAssignments.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          No substitute assignments found for the selected filter.
        </Alert>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: { xs: 'auto', sm: 'visible' },
            [theme.breakpoints.down('sm')]: {
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
            },
          }}
        >
          <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}>
                  Class
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', md: 'table-cell' },
                }}>
                  Original Teacher
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}>
                  Substitute Teacher
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}>
                  Period
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', lg: 'table-cell' },
                }}>
                  Reason
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssignments.map((assignment) => {
                const status = getAssignmentStatus(assignment);
                const canEdit = status === 'pending';
                const canCancel = status === 'pending';

                return (
                  <TableRow key={assignment.id}>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {assignment.classes?.name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {assignment.classes?.shift}
                        </Typography>
                        {/* Show original teacher on mobile */}
                        {isMobile && (
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                          >
                            Original: {assignment.original_teacher?.first_name} {assignment.original_teacher?.last_name}
                          </Typography>
                        )}
                        {/* Show reason on mobile */}
                        {isMobile && assignment.reason && (
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                          >
                            Reason: {assignment.reason}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {assignment.original_teacher?.first_name} {assignment.original_teacher?.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {assignment.substitute_teacher?.first_name} {assignment.substitute_teacher?.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {dayjs(assignment.start_date).format('MMM D')} - {dayjs(assignment.end_date).format('MMM D, YYYY')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {dayjs(assignment.end_date).diff(dayjs(assignment.start_date), 'day') + 1} days
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {assignment.reason}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Chip
                        label={STATUS_LABELS[status]}
                        color={STATUS_COLORS[status]}
                        size="small"
                        variant="filled"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 0.5, sm: 1 },
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                      }}>
                        {assignment.notes && (
                          <Tooltip title={assignment.notes}>
                            <IconButton 
                              size="small"
                              sx={{ padding: { xs: 0.5, sm: 1 } }}
                            >
                              <InfoIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title="Edit Assignment">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditAssignment(assignment)}
                              sx={{ padding: { xs: 0.5, sm: 1 } }}
                            >
                              <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {status === 'pending' && (
                          <Tooltip title="Mark as Completed">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleMarkCompleted(assignment)}
                              sx={{ padding: { xs: 0.5, sm: 1 } }}
                            >
                              <CheckCircleIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canCancel && (
                          <Tooltip title="Cancel Assignment">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleCancelAssignment(assignment)}
                              sx={{ padding: { xs: 0.5, sm: 1 } }}
                            >
                              <CancelIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

        </Box>
      ) : (
        // Coverage Requests Tab
        <Box>
          <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Filter</InputLabel>
                <Select
                  value={coverageFilter}
                  label="Filter"
                  onChange={(e) => setCoverageFilter(e.target.value)}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>All Requests</MenuItem>
                  <MenuItem value="pending" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Pending</MenuItem>
                  <MenuItem value="approved" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Approved</MenuItem>
                  <MenuItem value="assigned" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Assigned</MenuItem>
                  <MenuItem value="rejected" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {coverageRequestsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
              <CircularProgress size={isMobile ? 32 : 40} />
            </Box>
          ) : filteredCoverageRequests.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              No coverage requests found for the selected filter.
            </Alert>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                overflowX: { xs: 'auto', sm: 'visible' },
                [theme.breakpoints.down('sm')]: {
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                  },
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Teacher
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Class
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Period
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Reason
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      Substitute
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCoverageRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {request.teacher?.first_name} {request.teacher?.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {request.classes?.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {request.classes?.shift}
                          </Typography>
                          {/* Show reason on mobile */}
                          {isMobile && request.reason && (
                            <Typography 
                              variant="caption" 
                              color="textSecondary"
                              sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                            >
                              Reason: {request.reason}
                            </Typography>
                          )}
                          {/* Show substitute on mobile */}
                          {isMobile && (
                            <Typography 
                              variant="caption" 
                              color="textSecondary"
                              sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                            >
                              Substitute: {request.substitute_teacher 
                                ? `${request.substitute_teacher.first_name} ${request.substitute_teacher.last_name}`
                                : 'Not assigned'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {dayjs(request.start_date).format('MMM D')} - {dayjs(request.end_date).format('MMM D, YYYY')}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {dayjs(request.end_date).diff(dayjs(request.start_date), 'day') + 1} day{dayjs(request.end_date).diff(dayjs(request.start_date), 'day') !== 0 ? 's' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        display: { xs: 'none', lg: 'table-cell' },
                      }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {request.reason}
                          </Typography>
                          {request.description && (
                            <Tooltip title={request.description}>
                              <IconButton 
                                size="small"
                                sx={{ padding: { xs: 0.5, sm: 1 } }}
                              >
                                <InfoIcon fontSize={isMobile ? 'small' : 'medium'} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Chip
                          label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          color={request.status === 'pending' ? 'warning' : request.status === 'assigned' ? 'success' : request.status === 'rejected' ? 'error' : 'info'}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                        {request.status === 'rejected' && request.rejection_reason && (
                          <Box sx={{ 
                            mt: 1, 
                            p: { xs: 1, sm: 1.5 }, 
                            backgroundColor: 'grey.100', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'error.main'
                          }}>
                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'medium', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              Rejection Reason:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {request.rejection_reason}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        display: { xs: 'none', md: 'table-cell' },
                      }}>
                        {request.substitute_teacher ? (
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {request.substitute_teacher.first_name} {request.substitute_teacher.last_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: { xs: 0.5, sm: 1 },
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                        }}>
                          {request.status === 'pending' && (
                            <>
                              <Tooltip title="Approve & Assign Substitute">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => handleApproveCoverageRequest(request)}
                                  sx={{ padding: { xs: 0.5, sm: 1 } }}
                                >
                                  <CheckCircleIcon fontSize={isMobile ? 'small' : 'medium'} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Request">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRejectCoverageRequest(request)}
                                  sx={{ padding: { xs: 0.5, sm: 1 } }}
                                >
                                  <CancelIcon fontSize={isMobile ? 'small' : 'medium'} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Create/Edit Assignment Form */}
      <SubstituteAssignmentForm
        open={showForm}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        existingAssignment={editingAssignment}
      />

      {/* Cancel Confirmation Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => setCancelDialogOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Cancel Substitute Assignment
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to cancel this substitute assignment?
          </Typography>
          {assignmentToCancel && (
            <Box sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, backgroundColor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Class:</strong> {assignmentToCancel.classes?.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Substitute:</strong> {assignmentToCancel.substitute_teacher?.first_name} {assignmentToCancel.substitute_teacher?.last_name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Period:</strong> {dayjs(assignmentToCancel.start_date).format('MMM D')} - {dayjs(assignmentToCancel.end_date).format('MMM D, YYYY')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '12px 24px' },
            }}
          >
            Keep Assignment
          </Button>
          <Button 
            onClick={confirmCancelAssignment} 
            color="error" 
            variant="contained"
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '12px 24px' },
            }}
          >
            Cancel Assignment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Substitute Dialog */}
      <Dialog 
        open={assignSubstituteDialogOpen} 
        onClose={() => setAssignSubstituteDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Assign Substitute Teacher
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="body2" 
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Select a substitute teacher for this coverage request:
          </Typography>
          {requestToAssign && (
            <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, backgroundColor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Teacher:</strong> {requestToAssign.teacher?.first_name} {requestToAssign.teacher?.last_name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Class:</strong> {requestToAssign.classes?.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Period:</strong> {dayjs(requestToAssign.start_date).format('MMM D')} - {dayjs(requestToAssign.end_date).format('MMM D, YYYY')}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Reason:</strong> {requestToAssign.reason}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Substitute Teacher *</InputLabel>
            <Select
              value={selectedSubstitute}
              label="Substitute Teacher *"
              onChange={(e) => setSelectedSubstitute(e.target.value)}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              {substituteTeachers.map((teacher) => (
                <MenuItem 
                  key={teacher.id} 
                  value={teacher.id}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {teacher.first_name} {teacher.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={() => setAssignSubstituteDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '12px 24px' },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmAssignSubstitute} 
            variant="contained" 
            disabled={!selectedSubstitute}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '12px 24px' },
            }}
          >
            Assign Substitute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog 
        open={rejectionDialogOpen} 
        onClose={() => setRejectionDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Reject Coverage Request
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="body2" 
            sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Please provide a reason for rejecting this coverage request:
          </Typography>
          {requestToReject && (
            <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, backgroundColor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Teacher:</strong> {requestToReject.teacher?.first_name} {requestToReject.teacher?.last_name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Class:</strong> {requestToReject.classes?.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                <strong>Period:</strong> {dayjs(requestToReject.start_date).format('MMM D')} - {dayjs(requestToReject.end_date).format('MMM D, YYYY')}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
            placeholder="Explain why this request cannot be accommodated..."
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={() => setRejectionDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '12px 24px' },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmRejectRequest} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason.trim()}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '12px 24px' },
            }}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}

export default SubstituteManagement;
